import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { linksAPI, usersAPI, tokenAPI, adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Key, Search, MoreVertical, Pencil, Trash2, Link2, ExternalLink, Eye, X, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Users, Layers } from 'lucide-react';

export default function LinkManagementPage() {
  const { user: currentUser } = useAuth();
  const [links, setLinks] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showAddToken, setShowAddToken] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [deleteLink, setDeleteLink] = useState(null);
  const [viewLink, setViewLink] = useState(null);
  const [tokenForm, setTokenForm] = useState({ token: '', user_id: '' });
  const [editForm, setEditForm] = useState({ name: '', url: '' });
  const [redeeming, setRedeeming] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grouped'
  const [selectedObcName, setSelectedObcName] = useState(null);
  const [obcUsersData, setObcUsersData] = useState(null);
  const [loadingObcUsers, setLoadingObcUsers] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [linksRes, usersRes] = await Promise.all([
        linksAPI.getAll(),
        currentUser?.role !== 'user' ? usersAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      setLinks(linksRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    }
  };

  const filteredLinks = links.filter(l => {
    const searchLower = search.toLowerCase();
    const u = users.find(u => u.user_id === l.user_id);
    const userName = u?.name?.toLowerCase() || '';
    const userEmail = u?.email?.toLowerCase() || '';
    return (
      l.name?.toLowerCase().includes(searchLower) ||
      l.url?.toLowerCase().includes(searchLower) ||
      userName.includes(searchLower) ||
      userEmail.includes(searchLower)
    );
  });

  const getUserName = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? u.name : userId;
  };

  const getUserNameWithEmail = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? `${u.name} (${u.email})` : userId;
  };

  const getUserEmail = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? u.email : '';
  };

  // Sorting logic
  const sortedLinks = [...filteredLinks].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue, bValue;
    switch (sortConfig.key) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'user_id':
        aValue = getUserName(a.user_id)?.toLowerCase() || '';
        bValue = getUserName(b.user_id)?.toLowerCase() || '';
        break;
      case 'created_by':
        aValue = getUserName(a.created_by)?.toLowerCase() || '';
        bValue = getUserName(b.created_by)?.toLowerCase() || '';
        break;
      case 'created_at':
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const handleRedeemForUser = async () => {
    if (!tokenForm.token.trim()) {
      toast.error('Inserisci un codice token');
      return;
    }
    if (!tokenForm.user_id) {
      toast.error('Seleziona un utente');
      return;
    }
    setRedeeming(true);
    try {
      const res = await tokenAPI.redeemForUser(tokenForm.token.trim(), tokenForm.user_id);
      toast.success(res.data.message || 'OBC aggiunto con successo');
      setShowAddToken(false);
      setTokenForm({ token: '', user_id: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Token non valido');
    } finally {
      setRedeeming(false);
    }
  };

  const handleEdit = (link) => {
    setEditLink(link);
    setEditForm({ name: link.name, url: link.url });
  };

  const handleSaveEdit = async () => {
    try {
      await linksAPI.update(editLink.link_id, editForm);
      toast.success('OBC aggiornato');
      setEditLink(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleDelete = async () => {
    try {
      await linksAPI.delete(deleteLink.link_id);
      toast.success('OBC eliminato');
      setDeleteLink(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  // Group links by name for grouped view
  const groupedLinks = links.reduce((acc, link) => {
    if (!acc[link.name]) {
      acc[link.name] = [];
    }
    acc[link.name].push(link);
    return acc;
  }, {});

  const groupedArray = Object.entries(groupedLinks).map(([name, instances]) => ({
    name,
    count: instances.length,
    instances
  })).filter(group => 
    group.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleViewObcUsers = async (obcName) => {
    if (currentUser?.role !== 'root' && currentUser?.role !== 'administrator') {
      toast.error('Solo gli amministratori possono vedere questa informazione');
      return;
    }
    setSelectedObcName(obcName);
    setLoadingObcUsers(true);
    try {
      const res = await adminAPI.getObcByName(obcName);
      setObcUsersData(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento utenti');
      setObcUsersData(null);
    } finally {
      setLoadingObcUsers(false);
    }
  };

  // Get available users for OBC assignment
  const availableUsers = currentUser?.role === 'integrator'
    ? users.filter(u => u.integrator_id === currentUser.user_id || u.user_id === currentUser.user_id)
    : users;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="links-page-title">
            Gestione OBC
          </h1>
          <p className="text-muted-foreground mt-1">{links.length} link configurati</p>
        </div>
        <Button
          data-testid="add-token-btn"
          onClick={() => setShowAddToken(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Key className="w-4 h-4 mr-2" /> Aggiungi con Token
        </Button>
      </div>

      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                <Link2 className="w-5 h-5 inline mr-2" />
                Elenco OBC
              </CardTitle>
              {(currentUser?.role === 'root' || currentUser?.role === 'administrator') && (
                <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
                  <TabsList className="h-8">
                    <TabsTrigger value="list" className="text-xs px-2">
                      <Link2 className="w-3 h-3 mr-1" /> Lista
                    </TabsTrigger>
                    <TabsTrigger value="grouped" className="text-xs px-2">
                      <Layers className="w-3 h-3 mr-1" /> Per Nome
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca OBC..."
                data-testid="search-links"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {viewMode === 'grouped' && (currentUser?.role === 'root' || currentUser?.role === 'administrator') ? (
            /* Grouped View */
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Nome OBC</TableHead>
                    <TableHead>Istanze</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedArray.map((group) => (
                    <TableRow key={group.name} className="border-border">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                            <Layers className="w-4 h-4 text-primary" />
                          </div>
                          <span className="font-medium">{group.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{group.count} utenti</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewObcUsers(group.name)}
                        >
                          <Users className="w-4 h-4 mr-1" /> Vedi Utenti
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {groupedArray.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        Nessun OBC trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* List View */
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead 
                      className="cursor-pointer hover:bg-secondary/50 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Nome
                        <SortIcon columnKey="name" />
                      </div>
                    </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('user_id')}
                  >
                    <div className="flex items-center">
                      Assegnato a
                      <SortIcon columnKey="user_id" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('created_by')}
                  >
                    <div className="flex items-center">
                      Creato da
                      <SortIcon columnKey="created_by" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Data
                      <SortIcon columnKey="created_at" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLinks.map((link) => (
                  <TableRow key={link.link_id} className="border-border" data-testid={`link-row-${link.link_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                          <Link2 className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-medium">{link.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex flex-col">
                        <span>{getUserName(link.user_id)}</span>
                        <span className="text-xs opacity-70">({getUserEmail(link.user_id)})</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{getUserName(link.created_by)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(link.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`link-actions-${link.link_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover border-border">
                          <DropdownMenuItem onClick={() => setViewLink(link)} className="cursor-pointer">
                            <Eye className="w-4 h-4 mr-2" /> Visualizza
                          </DropdownMenuItem>
                          {currentUser?.role && ['root', 'administrator'].includes(currentUser.role) && (
                            <DropdownMenuItem onClick={() => handleEdit(link)} className="cursor-pointer">
                              <Pencil className="w-4 h-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => setDeleteLink(link)}
                            className="cursor-pointer text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {sortedLinks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Nessun OBC trovato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Add with Token Dialog */}
      <Dialog open={showAddToken} onOpenChange={setShowAddToken}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Aggiungi OBC con Token</DialogTitle>
            <DialogDescription>
              Inserisci il codice token e seleziona l'utente a cui assegnare l'OBC
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Codice Token</Label>
              <Input
                data-testid="admin-token-input"
                placeholder="Inserisci il codice token..."
                value={tokenForm.token}
                onChange={(e) => setTokenForm({ ...tokenForm, token: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Assegna a Utente</Label>
              <Select value={tokenForm.user_id} onValueChange={(v) => setTokenForm({ ...tokenForm, user_id: v })}>
                <SelectTrigger data-testid="admin-token-user">
                  <SelectValue placeholder="Seleziona utente" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {availableUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToken(false)}>Annulla</Button>
            <Button 
              data-testid="save-admin-token" 
              onClick={handleRedeemForUser} 
              className="bg-primary text-primary-foreground"
              disabled={redeeming}
            >
              {redeeming ? 'Aggiunta in corso...' : 'Aggiungi OBC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editLink} onOpenChange={() => setEditLink(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Modifica OBC</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome OBC</Label>
              <Input
                data-testid="edit-link-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                data-testid="edit-link-url"
                value={editForm.url}
                onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLink(null)}>Annulla</Button>
            <Button data-testid="save-edit-link" onClick={handleSaveEdit} className="bg-primary text-primary-foreground">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteLink} onOpenChange={() => setDeleteLink(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il link <strong>{deleteLink?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-link"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iframe Viewer Dialog */}
      {viewLink && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          <div className="h-14 border-b border-border flex items-center justify-between px-4 bg-background/95 backdrop-blur">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewLink(null)}
                data-testid="close-iframe-viewer"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                <Link2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>{viewLink.name}</h3>
                <p className="text-xs text-muted-foreground">Assegnato a: {getUserName(viewLink.user_id)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewLink(null)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-1 bg-muted/20">
            <iframe
              src={viewLink.url}
              title={viewLink.name}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="link-iframe-admin"
            />
          </div>
        </div>
      )}

      {/* View OBC Users Dialog (for grouped view) */}
      <Dialog open={!!selectedObcName} onOpenChange={() => { setSelectedObcName(null); setObcUsersData(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <Users className="w-5 h-5 inline mr-2" />
              Utenti con accesso a "{selectedObcName}"
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingObcUsers ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : obcUsersData ? (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-sm"><strong>Istanze OBC:</strong> {obcUsersData.instances?.length || 0}</p>
                  <p className="text-sm"><strong>Utenti Totali:</strong> {obcUsersData.users_count}</p>
                </div>
                {obcUsersData.users?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Tipo Accesso</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {obcUsersData.users.map((u) => (
                        <TableRow key={u.user_id} className="border-border">
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell className="text-muted-foreground">{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{u.role}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              u.access_type === 'administrator' ? 'text-purple-500 border-purple-500/30' :
                              u.access_type === 'integrator' ? 'text-blue-500 border-blue-500/30' :
                              'text-green-500 border-green-500/30'
                            }>
                              {u.access_type === 'administrator' ? 'Admin' :
                               u.access_type === 'integrator' ? 'Integratore' : 'Diretto'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessun utente trovato
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Errore nel caricamento dei dati
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedObcName(null); setObcUsersData(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
